import { flash } from "@xola/ui-kit";
import axios, { AxiosRequestConfig } from "axios";
import { FormField, InputField } from "common";
import { FormikProps } from "formik";
import React, { forwardRef, Ref, useState } from "react";
import { useMutation } from "react-query";
import { Permission } from "types";
import { api } from "../../../api/api";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useVisibility } from "../../../hooks/useVisibility";
import {
    changePasswordInitialValues,
    ChangePasswordPayload,
    changePasswordValidationSchema,
    PASSWORD_RULES_HINT,
} from "../../../models/ChangePassword";
import { isMultiFactorAuthenticationEnabled } from "../../../models/User";
import { AutoCompleteType, Form } from "../../Common/Form";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { MultiFactorAuthenticationInfoModal } from "./MultiFactorAuthenticationCheck";
import { MultiFactorAuthenticationModal } from "./MultiFactorAuthenticationModal";
import { signOut } from "@/auth/signOut";

export const ChangePasswordForm = forwardRef((props, ref: Ref<FormikProps<ChangePasswordPayload>>) => {
    const user = useUser();
    const seller = useSeller();
    const config: AxiosRequestConfig = { ignoreUnauthorizedErrorInterceptor: true };
    const [changePasswordPayload, setChangePasswordPayload] = useState<ChangePasswordPayload>();
    const { isGranted } = useAuthorization();
    const hasMFAAccess = isGranted(Permission.ADD_MULTI_FACTOR_AUTHENTICATION_VIEW);
    const isMFAEnabled = isMultiFactorAuthenticationEnabled(user);
    const [isMultiFactorAuthenticationModalOpen, setMultiFactorAuthenticationModalOpen] = useState(false);
    const [showMFAInfo, { toggle: toggleshowMFAInfo }] = useVisibility(false);

    const { mutateAsync: updatePassword, isLoading } = useMutation(
        async (changePasswordPayload: ChangePasswordPayload) => {
            return api.put(`users/${user.id}/changePassword?seller=${seller.id}`, changePasswordPayload, config);
        },
    );

    const handleChangePasswordFormData = async () => {
        if (!changePasswordPayload) {
            return false;
        }

        try {
            await updatePassword(changePasswordPayload);
        } catch {
            // The interceptor has already flashed what the API said. Nothing is closed here, so a
            // rejected authentication code can be entered again.
            return false;
        }

        setChangePasswordPayload(undefined);
        flash.success("Password changed successfully");

        // The account cache is deliberately left alone. Changing the password rotates the API key on
        // the server, so the key this client still holds is dead: refetching the account would sign a
        // request with it, take a 401, and land an authentication error beside the success message.
        // signOut() navigates away, which discards the cache anyway.
        await signOut();

        return true;
    };

    const handleSubmit = async (values: ChangePasswordPayload) => {
        if (hasMFAAccess && !isMFAEnabled) {
            toggleshowMFAInfo();
            return;
        }

        setChangePasswordPayload(values);
    };

    const handleMultiFactorAuthenticationCodeSubmit = async (code: string) => {
        if (code) {
            config.headers = { ...config.headers, "X-AUTH-CODE": code };
            await handleChangePasswordFormData();
        }
    };

    const handleConfirmChangePasswordButton = async () => {
        if (hasMFAAccess && isMFAEnabled) {
            try {
                if (changePasswordPayload) {
                    await updatePassword(changePasswordPayload);
                }
            } catch (error: unknown) {
                if (axios.isAxiosError(error) && error.response?.status === 450) {
                    setMultiFactorAuthenticationModalOpen(true);
                    return;
                }

                // The API refused the password itself, before any code was asked for. The interceptor
                // has flashed why, so close the prompt and leave the form open to correct it.
                setChangePasswordPayload(undefined);
            }

            return;
        }

        const didChangePassword = await handleChangePasswordFormData();
        if (!didChangePassword) {
            setChangePasswordPayload(undefined);
        }
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validateOnBlur={false}
            innerRef={ref}
            validationSchema={changePasswordValidationSchema}
            initialValues={changePasswordInitialValues}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit }: any) => {
                return (
                    <Form autoComplete={AutoCompleteType.OFF}>
                        <div className="space-y-4">
                            <FormField name="password" label="New Password">
                                <InputField isRequired type="password" />
                                <p className="text-sm text-secondary">{PASSWORD_RULES_HINT}</p>
                            </FormField>
                            <FormField name="confirmPassword" label="Confirm New Password">
                                <InputField isRequired type="password" />
                            </FormField>
                            <SecondaryButton onClick={handleSubmit}>Change</SecondaryButton>
                        </div>
                        <PromptModal
                            title="Warning"
                            isLoading={isLoading}
                            isOpen={!!changePasswordPayload}
                            onConfirm={handleConfirmChangePasswordButton}
                            onClose={() => setChangePasswordPayload(undefined)}
                        >
                            Changing Password will logout from all active sessions. Are you sure you want to change
                            password?
                        </PromptModal>
                        <MultiFactorAuthenticationModal
                            isOpen={isMultiFactorAuthenticationModalOpen}
                            onClose={() => {
                                setMultiFactorAuthenticationModalOpen(false);
                                setChangePasswordPayload(undefined);
                            }}
                            onSuccess={handleMultiFactorAuthenticationCodeSubmit}
                        />

                        <MultiFactorAuthenticationInfoModal
                            isOpen={showMFAInfo}
                            onClose={toggleshowMFAInfo}
                            onConfigure={toggleshowMFAInfo}
                        />
                    </Form>
                );
            }}
        </XolaFormik>
    );
});
